
import { GoogleGenAI, Type } from "@google/genai";

/**
 * geminiService.ts
 * Servicii integrate cu Google Gemini API pentru asistență juridică, analiză de documente și briefing zilnic.
 * Respectă ghidurile oficiale @google/genai.
 */

const SECURITY_SHIELD = `
REGLĂ DE SECURITATE SUPREMĂ (LEXSMART PROTOCOL):
1. Ești LexSmart AI. Nu ieși din contextul juridic românesc.
2. Folosește legislatie.just.ro pentru a verifica legile.
3. Răspunde exclusiv în Limba Română.
4. CONFIDENȚIALITATE: Nu include NICIODATĂ nume de persoane, CNP-uri, adrese sau date private din documente în interogările externe (Google Search). Caută doar principii juridice, numere de articole și spețe anonimizate. Protejează datele clientului cu orice preț.
`;

// Obținerea asistenței juridice cu Grounding în Google Search
export const getLegalAdvice = async (prompt: string, context: any) => {
  // Inițializare fresh a instanței AI cu cheia din mediu
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  
  let systemInstruction = `Ești LexSmart AI, asistent juridic de elită. Contextul cabinetului: ${JSON.stringify({
    events: context.events,
    cases: context.cases,
    user: context.user
  })}. ${SECURITY_SHIELD}`;

  if (context.ragContext) {
    systemInstruction += `\n\nInformații regăsite semantic din documentele utilizatorului (RAG):\n${context.ragContext}\n\nTe rog să folosești cu prioritate aceste informații pentru a răspunde la întrebare dacă sunt relevante.`;
  }

  const response = await ai.models.generateContent({
    model: 'gemini-1.5-pro',
    contents: prompt,
    config: {
      tools: [{ googleSearch: {} }],
      systemInstruction,
      temperature: 0.1,
    }
  });
  return response;
};

// Sincronizarea statusului dosarului prin căutare pe portal.just.ro
export const syncCaseWithPortalStatus = async (caseNumber: string) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: `Caută status dosar ${caseNumber} pe portal.just.ro. Extrage numărul, stadiul procesual, rezoluția și următorul termen de judecată.`,
    config: { 
      tools: [{ googleSearch: {} }],
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          status: { type: Type.STRING },
          resolution: { type: Type.STRING },
          nextHearing: { type: Type.STRING, description: 'Data în format ISO sau YYYY-MM-DD' },
        }
      }
    }
  });
  try {
    return JSON.parse(response.text || '{}');
  } catch {
    return { status: "Indisponibil", resolution: "Eroare la parsarea portalului" };
  }
};

// Analiza documentelor și extragerea termenelor procedurale (PDF/Img)
export const analyzeDocumentWithDeadlines = async (fileName: string, base64Data: string, mimeType: string, preExtractedText?: string) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  
  let apiMimeType = mimeType;
  if (mimeType.includes('wordprocessingml') || mimeType.includes('msword')) {
    apiMimeType = 'text/plain'; 
  }

  const basePrompt = `Ești un asistent juridic de elită care analizează acest document juridic (${fileName}).
Misiunea ta este să diseci acest document pentru a ajuta avocatul:

1. REZUMAT: Creează un rezumat structurat și detaliat (Ex: Obiectul cererii, Situația de fapt, Argumente juridice). Fii precis și la obiect.
2. ENTITĂȚI: Extrage absolut TOATE entitățile specifice găsite în text: Nume de persoane/companii implicate, Instanțe/Instituții, Articole de Lege invocate și Datele calendaristice cheie.
3. CONFLICTE & EXCEPȚII: Identifică posibile conflicte legale, excepții care ar putea fi invocate (ex: prescripție, tardivitate, necompetență) pe baza textului.
4. PLAN DE ACȚIUNE: Generează o listă scurtă cu pașii recomandați de urmat pentru acest dosar/document (ex: "De depus întâmpinare până la data de...", "De verificat dovada comunicării", etc.).
5. Răspunde EXCLUSIV în Limba Română.

Răspunde STRICT în acest format JSON valid:
{
  "type": "tipul documentului",
  "summary": "rezumatul structurat pe paragrafe (folosește \\n pentru linii noi)",
  "entities": {
    "persons": ["Nume 1", "Nume 2"],
    "institutions": ["Instituția 1", "Firma 2"],
    "laws": ["Art. 1", "Legea 2"],
    "dates": ["Data 1", "Data 2"]
  },
  "analysis": {
    "actionPlan": ["Pasul 1", "Pasul 2"],
    "legalConflicts": ["Conflict/Excepție 1", "Conflict/Excepție 2"],
    "keyInsights": ["Observație cheie 1", "Observație cheie 2"]
  },
  "deadlines": [{"date": "YYYY-MM-DD", "description": ""}]
}`;


  const extractJsonFromText = (text: string) => {
    try {
      const match = text.match(/\{[\s\S]*\}/);
      if (match) {
        return JSON.parse(match[0]);
      }
      return JSON.parse(text.replace(/```json/g, '').replace(/```/g, '').trim());
    } catch (e) {
      console.warn("Eroare la parsarea JSON-ului din răspunsul AI:", e);
      return {};
    }
  };

  try {
    if (preExtractedText) {
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: `${basePrompt}\n\nText document:\n${preExtractedText}`,
      });
      return extractJsonFromText(response.text || "{}");
    } else {
      const cleanBase64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: [
          { inlineData: { data: cleanBase64, mimeType: apiMimeType } },
          { text: basePrompt }
        ]
      });
      return extractJsonFromText(response.text || "{}");
    }
  } catch (error) {
    console.error("Eroare la analiza documentului:", error);
    throw error;
  }
};

// Analiza unei imagini (chitanță/factură) pentru a extrage date financiare
export const analyzeReceiptImage = async (base64Data: string, mimeType: string) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  
  const prompt = `Analizează această chitanță/factură. Extrage suma totală de plată (ca număr, fără valută) și o scurtă descriere a plății. Răspunde DOAR în format JSON valid: {"amount": 100, "description": "Taxă timbru..."}`;

  try {
    const cleanBase64 = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: [
        { inlineData: { data: cleanBase64, mimeType } },
        { text: prompt }
      ]
    });
    
    let text = response.text || "{}";
    text = text.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(text);
  } catch (error) {
    console.error("Eroare la analiza chitanței:", error);
    throw error;
  }
};

// Analiză text juridic pur (pentru Word Plugin sau Copy-Paste)
export const analyzeLegalText = async (text: string) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });

  const prompt = `
    ${SECURITY_SHIELD}
    
    Te rog să analizezi următorul text juridic și să oferi un rezumat structurat. 
    Dacă textul conține clauze abuzive sau riscuri, evidențiază-le la final.
    
    Text:
    """
    ${text}
    """
  `;

  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: prompt
  });
  return response.text;
};

// Generarea briefing-ului zilnic pentru avocat
export const generateDailyBriefing = async (events: any[], cases: any[]) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: `Generați un briefing strategic scurt (max 2 fraze) pentru un avocat care are astăzi ${events.length} evenimente în calendar și ${cases.length} dosare în lucru.`
  });
  return response.text;
};

// Redactarea automată de drafturi juridice
export const generateLegalDraft = async (type: string, context: string) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: `Redactează un/o ${type} juridic(ă) profesionist(ă), respectând rigoarea limbajului juridic din România. Context: ${context}. Textul trebuie să fie curat, fără markdown de cod, gata de inserat într-un editor de text.`,
    config: {
      systemInstruction: SECURITY_SHIELD
    }
  });
  return response.text;
};

// Rescrierea textului cu AI
export const refineLegalText = async (text: string, action: 'formal' | 'explain' | 'translate_en' | 'summarize' | 'expand') => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  
  let promptAction = "";
  switch(action) {
    case 'formal': promptAction = "Rescrie următorul text într-un limbaj juridic românesc strict, formal și extrem de profesionist. Nu adăuga informații noi, doar îmbunătățește tonul."; break;
    case 'explain': promptAction = "Explică următorul text juridic în termeni simpli, pe înțelesul unui client fără pregătire juridică. Păstrează sensul exact."; break;
    case 'translate_en': promptAction = "Tradu următorul text juridic în limba engleză (Legal English). Păstrează nuanțele juridice."; break;
    case 'summarize': promptAction = "Rezumă următorul text la esențial, păstrând doar faptele și argumentele cheie. Fii concis."; break;
    case 'expand': promptAction = "Dezvoltă următorul text, adăugând fraze de legătură specifice documentelor juridice și argumentând mai in detaliu ideea de bază, fără a inventa date factuale noi."; break;
  }

  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: `${promptAction}\n\nText original:\n"${text}"\n\nReturnează DOAR rezultatul cerut, fără alte introduceri sau explicații. Fără blocuri de cod markdown.`,
    config: {
      systemInstruction: SECURITY_SHIELD
    }
  });
  return response.text;
};

// Analiza performanței cabinetului
export const analyzePerformance = async (cases: any[], payments: any[]) => {
  const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash',
    contents: `Analizează performanța cabinetului bazată pe ${cases.length} dosare și fluxul financiar curent. Oferă un sfat strategic de management.`
  });
  return response.text;
};

export const generateEmbeddings = async (text: string): Promise<number[]> => {
  try {
    const ai = new GoogleGenAI({ apiKey: (import.meta as any).env.VITE_GOOGLE_AI_KEY });
    const response = await ai.models.embedContent({
      model: 'text-embedding-004',
      contents: text,
    });
    return response.embeddings?.[0]?.values || [];
  } catch (e) {
    console.error("Error generating embeddings:", e);
    return [];
  }
};

export const chunkText = (text: string, chunkSize: number = 1000, overlap: number = 200): string[] => {
  if (!text) return [];
  const words = text.split(/\s+/);
  const chunks: string[] = [];
  for (let i = 0; i < words.length; i += chunkSize - overlap) {
    chunks.push(words.slice(i, i + chunkSize).join(' '));
  }
  return chunks;
};
